/**
 * RecallsRegister (C-3) — recall & mock-recall register.
 * KPI cards + status/classification filters + table + "Initiate recall" dialog.
 * Route: /recalls
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PageHero } from '@edms/design-system';
import styles from './Recalls.module.css';
import {
  useRecallsQuery, useCreateRecall, STATUS_LABEL, CLASS_LABEL, type NewRecallInput,
} from './useRecalls';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function classClass(c: string) {
  return c === 'CLASS_I' ? styles.cls1 : c === 'CLASS_II' ? styles.cls2 : styles.cls3;
}

export function RecallsRegister() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [classification, setClassification] = useState('');
  const q = useRecallsQuery({ status: status || undefined, classification: classification || undefined });
  const create = useCreateRecall();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewRecallInput>({ productName: '', reason: '', classification: 'CLASS_II', markets: 'Saudi Arabia' });
  const [err, setErr] = useState<string | null>(null);

  const items = q.data?.items ?? [];
  const stats = q.data?.stats;

  async function submit() {
    setErr(null);
    if (!form.productName.trim() || !form.reason.trim()) { setErr('Product and reason are required.'); return; }
    try {
      const created = await create.mutateAsync(form);
      setOpen(false);
      setForm({ productName: '', reason: '', classification: 'CLASS_II', markets: 'Saudi Arabia' });
      navigate({ to: '/recalls/$id', params: { id: created.recallId } });
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : 'Could not initiate the recall.'); }
  }

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Quality Events"
        title="Recalls"
        subtitle="Product recalls & mock-recall exercises — classification, market reach, quantity reconciliation, effectiveness, and regulatory notification."
        actions={
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { setOpen(true); setErr(null); }}>
            Initiate recall
          </button>
        }
      />

      {stats && (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.open}</span><span className={styles.kpiLabel}>Open</span></div>
          <div className={`${styles.kpi} ${Number(stats.class1_open) > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{stats.class1_open}</span><span className={styles.kpiLabel}>Class I open</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.mock}</span><span className={styles.kpiLabel}>Mock recalls</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.avg_effectiveness}%</span><span className={styles.kpiLabel}>Avg effectiveness</span></div>
        </div>
      )}

      <div className={styles.filters}>
        <select aria-label="Status filter" value={status} onChange={(e) => setStatus(e.target.value)} className={styles.select}>
          <option value="">All statuses</option>
          {Object.keys(STATUS_LABEL).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select aria-label="Classification filter" value={classification} onChange={(e) => setClassification(e.target.value)} className={styles.select}>
          <option value="">All classes</option>
          <option value="CLASS_I">Class I</option><option value="CLASS_II">Class II</option><option value="CLASS_III">Class III</option>
        </select>
        <span className={styles.resultCount}>{items.length} recall{items.length === 1 ? '' : 's'}</span>
      </div>

      {q.isLoading ? (
        <div className={styles.empty}>Loading recalls…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>No recalls match the current filters.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr><th>Recall</th><th>Product / batch</th><th>Class</th><th>Status</th><th>Effectiveness</th><th>Markets</th><th>Opened</th></tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.recallId} className={styles.row} onClick={() => navigate({ to: '/recalls/$id', params: { id: r.recallId } })}>
                <td className={styles.mono}>{r.recallNumber}{r.isMock && <span className={styles.mockTag} title="Mock-recall exercise">MOCK</span>}</td>
                <td>{r.productName}{r.batchRefs ? <span className={styles.sub}> · {r.batchRefs}</span> : ''}</td>
                <td><span className={`${styles.pill} ${classClass(r.classification)}`}>{CLASS_LABEL[r.classification] ?? r.classification}</span></td>
                <td><span className={styles.statusPill}>{STATUS_LABEL[r.status] ?? r.status}</span></td>
                <td>{r.effectivenessPct == null ? '—' : `${r.effectivenessPct}%`}</td>
                <td>{r.markets ?? '—'}</td>
                <td>{fmtDate(r.openedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal aria-label="Initiate recall">
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>Initiate a recall</h2>
            {err && <div className={styles.err} role="alert">{err}</div>}
            <label className={styles.field}><span>Product *</span>
              <input type="text" value={form.productName} onChange={(e) => setForm({ ...form, productName: e.target.value })} placeholder="e.g. Paracetamol 500mg Tablets" /></label>
            <div className={styles.field2}>
              <label className={styles.field}><span>Batch(es)</span>
                <input type="text" value={form.batchRefs ?? ''} onChange={(e) => setForm({ ...form, batchRefs: e.target.value })} /></label>
              <label className={styles.field}><span>Markets</span>
                <input type="text" value={form.markets ?? ''} onChange={(e) => setForm({ ...form, markets: e.target.value })} /></label>
            </div>
            <div className={styles.field2}>
              <label className={styles.field}><span>Classification</span>
                <select value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })}>
                  <option value="CLASS_I">Class I (serious hazard)</option>
                  <option value="CLASS_II">Class II</option>
                  <option value="CLASS_III">Class III (unlikely hazard)</option>
                </select></label>
              <label className={styles.field}><span>Qty released</span>
                <input type="number" value={form.qtyReleased ?? ''} onChange={(e) => setForm({ ...form, qtyReleased: e.target.value === '' ? undefined : Number(e.target.value) })} /></label>
            </div>
            <label className={styles.fieldCheck}>
              <input type="checkbox" checked={!!form.isMock} onChange={(e) => setForm({ ...form, isMock: e.target.checked })} />
              <span>Mock-recall exercise (procedure test, no actual product hazard)</span>
            </label>
            <label className={styles.field}><span>Reason *</span>
              <textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></label>
            <div className={styles.modalActions}>
              <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void submit()} disabled={create.isPending}>
                {create.isPending ? 'Initiating…' : 'Initiate recall'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
